<template>
  <lf-scrollable-menu
    ref="scrollableMenu"
    class="do-tabs-scrollable-menu"
    @wheel.prevent="handleWheelScroll"
  >
    <div class="flex space-x-1">
      <div
        v-for="tab in tabs"
        :key="tab.name"
        class="cursor-pointer space-y-3 rounded-4 border border-b p-4"
        data-cy="service-tab"
        :class="
          tab.name === activeTab?.name
            ? 'border-divider'
            : 'border-gray-50 bg-gray-50'
        "
        @click.stop="emit('showTab', tab.name)"
      >
        <span
          class="leading-none font-semibold"
          :class="{
            'text-headline': isStepFinished(tab.name),
            'text-label': !isStepFinished(tab.name),
          }"
        >
          {{ tab.name }}
        </span>
        <div class="flex space-x-1">
          <button
            v-for="block in tab.blocks"
            :key="block.id"
            class="flex h-6 w-6 items-center justify-center rounded-full border-2"
            :class="borderColorMap[block.id]"
            @click="emit('showService', tab.name, block.id || '')"
          >
            <img
              :class="[
                {
                  'opacity-70 grayscale filter': !hasOutcome(block.id),
                },
                getIconSize(block.id),
              ]"
              :src="getServiceImage(block.id)"
              loading="lazy"
            />
          </button>
        </div>
      </div>
    </div>
  </lf-scrollable-menu>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";

import orderBy from "lodash/orderBy";

import {
  PROVIDER_OCROLUS_CFA,
  PROVIDER_OCROLUS_CFA_V2,
} from "@/helpers/constants";
import { useDeals } from "@/hooks/deals";
import { useUnderwritingLogs } from "@/hooks/underwriting";
import { useActiveStage, useActiveTab } from "@/hooks/workflow";
import { useWorkflowStagesInfo } from "@/hooks/workflow";

import LfScrollableMenu from "@/components/ui/LfScrollableMenu.vue";

const emit = defineEmits<{
  (eventName: "showService", tabName: string, blockId: string): void;
  (eventName: "showTab", tabName: string): void;
}>();

const { activeStage } = useActiveStage();

const { underwritingLogsWithServices } = useUnderwritingLogs();

const { isStepFinished } = useDeals();

const { activeTab } = useActiveTab();

const scrollableMenu = ref<InstanceType<typeof LfScrollableMenu> | null>(null);

const tabs = computed(() => activeStage.value?.tabs || []);
const workflowStagesInfo = useWorkflowStagesInfo();

const underwritingStageInfo = computed(
  () =>
    workflowStagesInfo.value.find(({ type }) => type === "underwriting")
      ?.blocks || []
);

const hasOutcome = computed(() => {
  return (blockId: string) => {
    const blockServices =
      underwritingStageInfo.value.find(({ id }) => blockId === id)
        ?.related_services || [];
    return underwritingLogsWithServices.value.some((item) =>
      blockServices.some((service) => {
        if (service.toLowerCase() === PROVIDER_OCROLUS_CFA) {
          return [PROVIDER_OCROLUS_CFA, PROVIDER_OCROLUS_CFA_V2].some(
            (provider) => item.service.toLowerCase() === provider.toLowerCase()
          );
        }
        return item.service.toLowerCase() === service.toLowerCase();
      })
    );
  };
});

const getServiceImage = computed(() => {
  return (blockId: string) => {
    const icon = underwritingStageInfo.value.find(
      ({ id }) => blockId === id
    )?.icon;

    return icon ? `/images/${icon}` : undefined;
  };
});

const borderColorMap = computed(() => {
  const newMap = underwritingStageInfo.value.reduce<Record<string, string>>(
    (map, { id, related_services }) => {
      const logs = underwritingLogsWithServices.value.filter((log) => {
        if (related_services.includes(PROVIDER_OCROLUS_CFA)) {
          return [...related_services, PROVIDER_OCROLUS_CFA_V2].includes(
            log.service
          );
        }

        return related_services.includes(log.service);
      });
      const log = orderBy(logs, "date", "desc")[0];

      if (!log) {
        map[id] = "";
      } else if (log.initiator === "user") {
        map[id] = "border-blue-500";
      } else {
        switch (log.outcome) {
          case "pass":
            map[id] = "border-success";
            break;
          case "fail":
            map[id] = "border-error";
            break;
          case "manual review":
            map[id] = "border-warning";
            break;
          default:
            map[id] = "";
        }
      }
      return map;
    },
    {}
  );
  return newMap;
});

const getIconSize = (id: string) =>
  ["middesk", "ekata", "inscribe", "persona", "experian", "equifax"].some(
    (service) => id.includes(service)
  )
    ? "h-3 w-3"
    : "h-4 h-4";

const handleWheelScroll = (e: WheelEvent) => {
  if (scrollableMenu.value?.menu) {
    e.preventDefault();

    const menu: HTMLElement = scrollableMenu.value.menu;
    menu.classList.remove("smooth-scroll");
    if (e.deltaX) {
      menu.scrollLeft += e.deltaX;
    }

    if (e.deltaY) {
      menu.scrollLeft += e.deltaY;
    }
  }
};
</script>
<style>
@reference "@/assets/tailwind.css";

.do-tabs-scrollable-menu .icon-wrapper-left,
.do-tabs-scrollable-menu .icon-wrapper-right {
  @apply top-auto;
  background: transparent !important;
}

.do-tabs-scrollable-menu .icon-wrapper-left {
  left: -50px !important;
}

.do-tabs-scrollable-menu .icon-wrapper-right {
  right: -50px !important;
}
</style>
